import { useState, useEffect } from "react";

interface TranscriptionTypingAnimationProps {
  messages?: string[];
  typingSpeed?: number; // ms per character when typing
  deletingSpeed?: number; // ms per character when deleting
  pauseBetweenMessages?: number; // ms pause after typing before deleting
}

export function TranscriptionTypingAnimation({
  messages = [
    "Deine Sprache wird transkribiert...",
    "Kontext wird geladen...",
    "Einen Moment bitte...",
  ],
  typingSpeed = 50,
  deletingSpeed = 30,
  pauseBetweenMessages = 1000,
}: TranscriptionTypingAnimationProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentMessage = messages[currentMessageIndex];

    if (!isDeleting && currentText === currentMessage) {
      // Finished typing current message, pause then start deleting
      const pauseTimeout = setTimeout(() => {
        setIsDeleting(true);
      }, pauseBetweenMessages);
      return () => clearTimeout(pauseTimeout);
    }

    if (isDeleting && currentText === "") {
      // Finished deleting, move to next message
      setIsDeleting(false);
      setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
      return;
    }

    // Type or delete one character
    const speed = isDeleting ? deletingSpeed : typingSpeed;
    const timeout = setTimeout(() => {
      setCurrentText((prev) => {
        if (isDeleting) {
          return prev.slice(0, -1);
        } else {
          return currentMessage.slice(0, prev.length + 1);
        }
      });
    }, speed);

    return () => clearTimeout(timeout);
  }, [
    currentText,
    currentMessageIndex,
    isDeleting,
    messages,
    typingSpeed,
    deletingSpeed,
    pauseBetweenMessages,
  ]);

  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <span className="whitespace-nowrap">
        {currentText}
        <span className="inline-block w-[2px] h-4 bg-primary ml-1 animate-pulse" />
      </span>
    </div>
  );
}
